<template>
  <ContentWrap>
    <el-alert
      title="这里的每一行都是一次约见：创建用户=发起方，目标用户=接收方。「确认状态」是对方是否受理，「状态」是行程本身（超时未响应会由系统关闭）。"
      type="info"
      :closable="false"
      class="mb-12px"
    />
    <el-form :model="query" inline>
      <el-form-item label="创建用户"
        ><BusinessLookupSelect v-model="query.userId" type="users" placeholder="请选择用户"
      /></el-form-item>
      <el-form-item label="目标用户"
        ><BusinessLookupSelect v-model="query.targetUserId" type="users" placeholder="请选择目标用户"
      /></el-form-item>
      <el-form-item label="状态">
        <el-select v-model="query.status" clearable class="!w-130px">
          <el-option
            v-for="(label, value) in statusLabels"
            :key="value"
            :label="label"
            :value="Number(value)"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="确认状态">
        <el-select v-model="query.acceptStatus" clearable class="!w-140px">
          <el-option
            v-for="dict in getIntDictOptions(DICT_TYPE.BOSSCIRCLE_VISIT_ACCEPT_STATUS)"
            :key="dict.value"
            :label="dict.label"
            :value="dict.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="开始时间">
        <el-date-picker
          v-model="timeRange"
          type="datetimerange"
          value-format="YYYY-MM-DD HH:mm:ss"
          start-placeholder="开始时间"
          end-placeholder="结束时间"
        />
      </el-form-item>
    <el-form-item>
      <el-button type="primary" @click="search">查询</el-button>
    </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list">
      <el-table-column prop="planNo" label="行程编号" min-width="150" />
      <el-table-column prop="title" label="标题" min-width="180" show-overflow-tooltip />
      <el-table-column label="创建用户" min-width="120"><template #default="scope"><BusinessLookupName type="users" :id="scope.row.userId" /></template></el-table-column>
      <el-table-column label="目标用户" min-width="120"><template #default="scope"><BusinessLookupName type="users" :id="scope.row.targetUserId" /></template></el-table-column>
      <el-table-column label="开始时间" width="180"
        ><template #default="scope">{{ fmt(scope.row.startTime) }}</template></el-table-column
      >
      <el-table-column label="确认状态" width="110">
        <template #default="scope">
          <dict-tag :type="DICT_TYPE.BOSSCIRCLE_VISIT_ACCEPT_STATUS" :value="scope.row.acceptStatus" />
        </template>
      </el-table-column>
      <el-table-column label="状态" width="100"
        ><template #default="scope">{{ statusLabels[scope.row.status] }}</template></el-table-column
      >
      <el-table-column label="操作" width="140">
        <template #default="scope">
          <el-button link type="primary" @click="openDetail(scope.row.id)">详情</el-button>
          <el-button
            v-if="scope.row.status === 0 || scope.row.status === 1"
            v-hasPermi="['bosscircle:visit-plan:manage']"
            link
            type="danger"
            @click="close(scope.row)"
            >关闭</el-button
          >
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="query.pageNo"
      v-model:limit="query.pageSize"
      :total="total"
      @pagination="load"
    />
  </ContentWrap>

  <Dialog v-model="detailVisible" title="行程详情" width="820px">
    <el-descriptions v-if="detail" :column="2" border>
      <el-descriptions-item label="行程编号">{{ detail.planNo }}</el-descriptions-item>
      <el-descriptions-item label="状态">{{ statusLabels[detail.status] }}</el-descriptions-item>
      <el-descriptions-item label="创建用户">{{ detail.userId }}</el-descriptions-item>
      <el-descriptions-item label="目标用户">{{ detail.targetUserId || '-' }}</el-descriptions-item>
      <el-descriptions-item label="推进舱ID">{{ detail.dealRoomId || '-' }}</el-descriptions-item>
      <el-descriptions-item label="时间"
        >{{ fmt(detail.startTime) }} 至 {{ fmt(detail.endTime) || '-' }}</el-descriptions-item
      >
      <el-descriptions-item label="确认状态">
        <dict-tag :type="DICT_TYPE.BOSSCIRCLE_VISIT_ACCEPT_STATUS" :value="detail.acceptStatus" />
      </el-descriptions-item>
      <el-descriptions-item label="受理时间">{{ fmt(detail.acceptTime) || '-' }}</el-descriptions-item>
      <el-descriptions-item label="约见理由" :span="2">{{ detail.meetMessage || '-' }}</el-descriptions-item>
      <el-descriptions-item label="拜访地址" :span="2">{{
        detail.address || '-'
      }}</el-descriptions-item>
      <el-descriptions-item label="坐标" :span="2"
        >{{ detail.longitude || '-' }}, {{ detail.latitude || '-' }}</el-descriptions-item
      >
      <el-descriptions-item label="签到时间">{{ fmt(detail.checkInTime) || '-' }}</el-descriptions-item>
      <el-descriptions-item label="签到位置">{{
        detail.checkInLocation || '-'
      }}</el-descriptions-item>
      <el-descriptions-item label="行程小结" :span="2">{{
        detail.summary || '-'
      }}</el-descriptions-item>
      <el-descriptions-item label="取消/关闭原因" :span="2">{{
        detail.cancelReason || '-'
      }}</el-descriptions-item>
    </el-descriptions>
  </Dialog>
</template>

<script setup lang="ts">
import * as VisitPlanApi from '@/api/business/visitPlan'
import { formatDate } from '@/utils/formatTime'
import { DICT_TYPE, getIntDictOptions } from '@/utils/dict'

defineOptions({ name: 'BossCircleVisitPlan' })
/** 后端把 LocalDateTime 下发成时间戳，列表与详情都统一格式化后再展示 */
const fmt = (value: any) => formatDate(value)
const message = useMessage()
const loading = ref(false)
const list = ref<any[]>([])
const total = ref(0)
const timeRange = ref<string[]>([])
const detailVisible = ref(false)
const detail = ref<any>()
const statusLabels: Record<number, string> = {
  0: '计划中',
  1: '已签到',
  2: '已完成',
  3: '已取消',
  4: '已关闭'
}
const query = reactive({
  pageNo: 1,
  pageSize: 10,
  userId: undefined,
  targetUserId: undefined,
  status: undefined,
  acceptStatus: undefined
})
const load = async () => {
  loading.value = true
  try {
    const data = await VisitPlanApi.getVisitPlanPage({
      ...query,
      beginTime: timeRange.value?.[0],
      endTime: timeRange.value?.[1]
    })
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}
const search = () => {
  query.pageNo = 1
  load()
}
const openDetail = async (id: number) => {
  detail.value = await VisitPlanApi.getVisitPlan(id)
  detailVisible.value = true
}
const close = async (row: any) => {
  const { value } = await message.prompt('请输入关闭原因', '关闭行程')
  await VisitPlanApi.closeVisitPlan({ id: row.id, version: row.version, reason: value })
  message.success('关闭成功')
  await load()
}
onMounted(load)
</script>
